const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

type ObjectCacheEntry<T> = {
    object: T;
    timeoutId: number;
};

type ObjectCacheDeleteEntryFn<T> = (object: T) => void;

export class ObjectCache<T> {

    private readonly deleteFn: ObjectCacheDeleteEntryFn<T>;
    private readonly inner: Map<string, ObjectCacheEntry<T>> = new Map();

    constructor(deleteFn: ObjectCacheDeleteEntryFn<T> = () => {}) {
        this.deleteFn = deleteFn;
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public has(key: string): boolean {
        this._resetTimeout(key);
        return this.inner.has(key);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public set(key: string, object: T): void {
        this._resetTimeout(key, object);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public get(key: string): T | undefined {
        this._resetTimeout(key);
        return this.inner.get(key)?.object;
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private _resetTimeout(key: string, object?: T): void {

        const entry = this.inner.get(key);

        if (!object && !entry) {
            return;
        }

        object = object ?? entry!.object;

        if (entry) {
            clearTimeout(entry.timeoutId);
        }

        const timeoutId = window.setTimeout(() => {
            const entry = this.inner.get(key);
            if (entry) {
                this.deleteFn(entry.object);
                this.inner.delete(key);
            }
        }, DEFAULT_TTL_MS);

        this.inner.set(key, { object: object, timeoutId });
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public dispose(): void {
        this.inner.forEach(entry => {
            clearTimeout(entry.timeoutId);
            this.deleteFn(entry.object);
        });
        this.inner.clear();
    }
}